import type { DeskAnalysis, EcmIssue, JiraFix, SectorDesk, StoreDrift } from 'api/sectorAlignmentApi';

export type Tagged<T> = T & { sd: SectorDesk };
export interface TabRows { stores: Tagged<StoreDrift>[]; ecm: Tagged<EcmIssue>[]; jira: Tagged<JiraFix>[] }

export const TABS = ['stores', 'ecm', 'jira'] as const;
export type Tab = (typeof TABS)[number];

export const STORES_GRID = 'grid grid-cols-[minmax(220px,1.3fr)_90px_minmax(280px,1fr)_80px] items-center gap-3 px-5';
export const ECM_GRID = 'grid grid-cols-[200px_90px_minmax(300px,1fr)_220px] items-center gap-3 px-5';
export const JIRA_GRID = 'grid grid-cols-[260px_90px_minmax(300px,1fr)] items-center gap-3 px-5';

const SEVERITY: EcmIssue['kind'][] = ['sector-without-ecm', 'unknown-sector', 'no-sector', 'multiple-sectors', 'shared-sector'];
export const list = <T,>(value: T[] | null | undefined): T[] => (Array.isArray(value) ? value : []);
const ticketCount = (s: { tickets?: unknown[] | null }) => list(s.tickets).length;

/** Rows of every desk analysed without error, each tagged with its desk; ECM cases stay most severe first. */
export const tabRows = (desks: DeskAnalysis[] | null | undefined): TabRows => {
  const ok = list(desks).filter((d) => d?.status === 'ok');
  const tag = <T,>(pick: (d: DeskAnalysis) => T[] | null | undefined) => ok.flatMap((d) => list(pick(d)).map((row) => ({ ...row, sd: d.sd })));
  return {
    stores: tag((d) => d.stores).sort((a, b) => ticketCount(b) - ticketCount(a)),
    ecm: tag((d) => d.ecm).sort((a, b) => SEVERITY.indexOf(a.kind) - SEVERITY.indexOf(b.kind)),
    jira: tag((d) => d.jira),
  };
};

export const firstFilledTab = (counts: Record<Tab, number>): Tab | null => TABS.find((tab) => counts[tab] > 0) ?? null;

const ecmText = (issue: EcmIssue): string[] =>
  'user' in issue ? [issue.user?.name, issue.user?.email ?? '', 'sector' in issue ? issue.sector : '']
    : [issue.sector, ...('users' in issue ? list(issue.users).map((u) => u.name) : [])];

/** Toolbar filters: desk (`'all'` or the desk id) and a case-insensitive search on names and ticket keys. */
export const filterRows = (rows: TabRows, desk: string, query: string): TabRows => {
  const q = query.trim().toLowerCase();
  const keep = <T extends { sd: SectorDesk }>(row: T, texts: (string | null | undefined)[]) =>
    (desk === 'all' || String(row.sd) === desk) && (!q || texts.some((text) => text?.toLowerCase().includes(q)));
  const keys = (s: { tickets?: { key: string }[] | null }) => list(s.tickets).map((ticket) => ticket.key);
  return {
    stores: rows.stores.filter((s) => keep(s, [s.store, ...keys(s)])),
    ecm: rows.ecm.filter((e) => keep(e, ecmText(e))),
    jira: rows.jira.filter((f) => keep(f, [f.store, ...keys(f)])),
  };
};

/** « SP1 » alone, « SP1 (5), SP2 (3) » when a store's tickets sit on several old sectors. */
export const fromSectorsLabel = (fromSectors: StoreDrift['fromSectors'] | null | undefined): string => {
  const sectors = list(fromSectors);
  if (sectors.length === 1) return sectors[0].sector || '—';
  return sectors.map((f) => `${f.sector || '—'} (${f.count})`).join(', ') || '—';
};
